import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  useTheme,
} from "@mui/material";
import { Modal, Input, Tag, message } from "antd";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import ApiCallService from "../../../../Service/ApiCallService";
import Spinner from "../../../../components/common/Spinner";
import { usePermission } from "../../../../permissions/usePermission";
// import theme from "../../../../theme/themeConfig";


const MotionBox = motion(Box);

const JourneyReview = () => {
  const S3URL = "https://vmsprojectdata.s3.ap-south-1.amazonaws.com/"

  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const { canByKey } = usePermission();
  const canApproveJourneyEdit = canByKey("USER_MANAGEMENT_USER_APPROVAL_APPROVE_JOURNEY_EDIT");
  const canDeclineJourneyEdit = canByKey("USER_MANAGEMENT_USER_APPROVAL_DECLINE_JOURNEY_EDIT");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    const fetchJourney = async () => {
      try {
        setLoading(true);
        const res = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/journey/${id}`
        );
        setData(res);
      } catch {
        message.error("Failed to load journey details");
      } finally {
        setLoading(false);
      }
    };

    fetchJourney();
  }, [id]);

  const approve = async () => {
    try {
      setLoading(true);
      await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/journey/${id}/approve`
      );
      message.success("Journey approved successfully");
      navigate(-1);
    } catch {
      message.error("Approval failed");
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) {
      return message.warning("Please enter rejection reason");
    }

    try {
      setLoading(true);
      await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/journey/${id}/reject`,
        { reason }
      );
      message.success("Journey rejected");
      navigate(-1);
    } catch {
      message.error("Rejection failed");
    } finally {
      setRejectOpen(false);
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <Box mt={10} textAlign="center">
        <Spinner loading />
      </Box>
    );
  }

  const { requestedData, existingData, action } = data;

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString();
  };

  const isRichTextEmpty = (value) => {
    if (!value) return true;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = DOMPurify.sanitize(value);

    return !wrapper.textContent.trim() && !wrapper.querySelector("img, video, iframe");
  };

  const renderRichText = (value, sx = {}) => {
    if (isRichTextEmpty(value)) return "-";

    return (
      <Box
        sx={{
          mt: 1,
          overflowWrap: "anywhere",
          "& p": { m: 0, mb: 1 },
          "& p:last-child": { mb: 0 },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            mt: 0,
            mb: 1,
            fontWeight: "inherit",
          },
          "& blockquote": {
            m: 0,
            mb: 1,
            pl: 2,
            borderLeft: "3px solid #D1D5DB",
            color: "text.secondary",
          },
          "& ul, & ol": { mt: 0, mb: 1, pl: 3 },
          ...sx,
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(value),
        }}
      />
    );
  };

  const renderCreateField = (label, value, options = {}) => (
    <MotionBox
      p={2}
      borderRadius={2}
      border="1px solid #E5E7EB"
      bgcolor="#FFFFFF"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {options.richText ? (
        renderRichText(value)
      ) : (
        <Typography mt={1}>{value || "-"}</Typography>
      )}
    </MotionBox>
  );

  const renderUpdateField = (label, oldVal, newVal, options = {}) => {
    const changed = oldVal !== newVal;

    return (
      <MotionBox
        p={2}
        borderRadius={2}
        border="1px solid"
        borderColor={changed ? "#FFE58F" : "#E5E7EB"}
        bgcolor={changed ? "#FFFBE6" : "#FFFFFF"}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>

        {options.richText ? (
          changed ? (
            <>
              {renderRichText(oldVal, {
                color: "#999",
                textDecoration: "line-through",
                "& *": { color: "inherit" },
              })}
              <Typography component="span" sx={{ display: "block", my: 1 }}>
                →
              </Typography>
              {renderRichText(newVal, {
                color: "#15803D",
                fontWeight: 600,
                "& *": { color: "inherit" },
              })}
              <Tag color="orange" style={{ marginLeft: 8 }}>
                Updated
              </Tag>
            </>
          ) : (
            renderRichText(newVal)
          )
        ) : (
          <Typography mt={1}>
            {changed ? (
              <>
                <span style={{ textDecoration: "line-through", color: "#999" }}>
                  {oldVal || "-"}
                </span>
                {" → "}
                <strong style={{ color: "#15803D" }}>{newVal || "-"}</strong>
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  Updated
                </Tag>
              </>
            ) : (
              newVal || "-"
            )}
          </Typography>
        )}
      </MotionBox>
    );
  };

  const renderField = (label, oldVal, newVal, options = {}) => {
    if (action === "CREATE") {
      return renderCreateField(label, newVal, options);
    }
    return renderUpdateField(label, oldVal, newVal, options);
  };

const getFileType = (url) => {
  const ext = url.split(".").pop().toLowerCase();

  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
  if (["mp4", "webm", "ogg"].includes(ext)) return "video";
  if (["pdf"].includes(ext)) return "pdf";

  return "other";
};

  return (
    <>
      <Paper elevation={0} variant="pageBg" sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", borderRadius: "50%" }}>
          <Typography variant="h4" sx={{ textAlign: "center", width: "100%" }} fontWeight="bold">
            Journey Review
          </Typography>

          <Tag color={action === "CREATE" ? "green" : "orange"}>
            {action === "CREATE" ? "New Journey" : "Updated Journey"}
          </Tag>
        </Box>

        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} md={6}>
            {renderField(
              "Position",
              existingData?.position,
              requestedData.position
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {renderField(
              "Description",
              existingData?.description,
              requestedData.description,
              { richText: true }
            )}
          </Grid>


          <Grid item xs={12} md={6}>
            {renderField(
              "Start Date",
              formatDate(existingData?.startDate),
              formatDate(requestedData.startDate)
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {renderField(
              "End Date",
              formatDate(existingData?.endDate),
              formatDate(requestedData.endDate)
            )}
          </Grid>



          <Grid item xs={12}>
            <Typography fontWeight={600} mb={1}>
              Documents
            </Typography>

 


<Box display="flex" gap={2} flexWrap="wrap">
  {requestedData.documents?.length ? (
    requestedData.documents.map((doc, i) => {
      const fileUrl = doc.startsWith("http") ? doc : `${S3URL}${doc}`;
      const type = getFileType(fileUrl);

      return (
        <Box key={i} width={120}>
          {type === "image" && (
            <img
              src={fileUrl}
              alt="doc"
              style={{
                width: "100%",
                height: 100,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          )}

          {type === "video" && (
            <video
              src={fileUrl}
              controls
              style={{
                width: "100%",
                height: 100,
                borderRadius: 8,
              }}
            />
          )}

       {type === "pdf" && (
  <Box
    sx={{
      width: "100%",
      height: 100,
      borderRadius: 2,
      overflow: "hidden",
      border: "1px solid #ddd",
    }}
  >
    <iframe
      src={`${fileUrl}#toolbar=0`}
      title="pdf-preview"
      width="100%"
      height="100%"
      style={{ border: "none" }}
    />
  </Box>
)}

          {type === "other" && (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              <Box
                sx={{
                  width: "100%",
                  height: 100,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #ddd",
                  borderRadius: 2,
                }}
              >
                📁 File
              </Box>
            </a>
          )}
        </Box>
      );
    })
  ) : (
    <Typography color="text.secondary">No documents</Typography>
  )}
</Box>

          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
          {canDeclineJourneyEdit && (
            <Button variant="outlined" size="medium" color="error" onClick={() => setRejectOpen(true)}>
              Reject
            </Button>
          )}

          {canApproveJourneyEdit && (
            <Button
              variant="contained"
              size="medium"
              sx={{ background: theme.palette.primary.main, color: "#fff" }}
              onClick={approve}
            >
              Approve
            </Button>
          )}
        </Box>
      </Paper>


      <Modal
        title="Reject Journey"
        open={rejectOpen}
        onCancel={() => {
          setRejectOpen(false);
          setReason("");
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setRejectOpen(false);
            setReason("");
          }}>
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            type="primary"
            onClick={() => {
              reject();
              setRejectOpen(false);
              setReason("");
            }}
            disabled={!reason.trim()}
          >
            Reject
          </Button>
        ]}
      >
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Enter rejection reason"
        />
      </Modal>

    </>
  );
};

export default JourneyReview;
